/**
 * Trava de execução de buscas.
 *
 * Existe porque as telas de busca só se protegiam de Enter repetido por acaso:
 * as guardas eram verificações de cache condicionadas a `resultado.length > 0`.
 * Quando a busca voltava vazia (ou falhava), o cache nunca batia e cada Enter
 * disparava uma request nova. Aqui a trava é por CHAVE e independe do resultado.
 *
 * Regras:
 * - request em voo para a MESMA chave → bloqueia (trava síncrona, não depende de
 *   prop/render chegar no componente filho);
 * - request em voo para uma chave DIFERENTE → libera e assume o lugar. É o que
 *   mantém `triggerFilterSearch` (marcar filtro durante uma busca) funcionando —
 *   a de-dup do `API.call` cancela a anterior de propósito;
 * - busca concluída (com OU sem resultado) → arma o cooldown para a mesma chave;
 * - busca que falhou ou foi cancelada → NÃO arma, senão o usuário fica sem
 *   conseguir re-tentar depois de um erro de rede.
 *
 * A chave deve conter tudo que caracteriza a intenção do usuário (termo + tipo
 * de busca + filtros). Mudou a chave, é uma ação intencional nova: libera na
 * hora, sem esperar o cooldown.
 *
 * O bloqueio é silencioso de propósito: a tela já está exibindo o resultado
 * daquela chave exata ("Não encontramos resultados…"), então não parece travado.
 */

export const DEFAULT_SEARCH_COOLDOWN_MS = 3000

const sameKey = (a, b) => a != null && a === b

export function createSearchGuard (options = {}) {
  const cooldownMs = options.cooldownMs != null ? options.cooldownMs : DEFAULT_SEARCH_COOLDOWN_MS
  const now = options.now || (() => Date.now())

  let runningKey = null
  let lastKey = null
  let lastAt = 0

  /** Pode executar a busca dessa chave agora? */
  const shouldRun = (key) => {
    if (sameKey(key, runningKey)) return false
    if (!sameKey(key, lastKey)) return true
    return now() - lastAt >= cooldownMs
  }

  /** Tenta tomar a trava. Retorna false quando a busca deve ser descartada. */
  const start = (key) => {
    if (!shouldRun(key)) return false
    runningKey = key
    return true
  }

  /** Busca concluída — com resultado, vazia ou "não encontrado". Arma o cooldown. */
  const settle = (key) => {
    if (!sameKey(key, runningKey)) return // resposta de uma busca já superada
    runningKey = null
    lastKey = key
    lastAt = now()
  }

  /** Busca falhou ou foi cancelada: solta a trava sem armar o cooldown. */
  const abort = (key) => {
    if (!sameKey(key, runningKey)) return
    runningKey = null
  }

  /** Invalida a última busca (ex.: usuário limpou o campo). */
  const reset = () => {
    runningKey = null
    lastKey = null
    lastAt = 0
  }

  /** Rehidrata a trava a partir de estado externo (Vuex), p/ sobreviver a remount. */
  const restore = (key, at) => {
    lastKey = key != null ? key : null
    lastAt = at || 0
  }

  /** Estado serializável da trava, para persistir fora do componente. */
  const snapshot = () => ({ key: lastKey, at: lastAt })

  return { shouldRun, start, settle, abort, reset, restore, snapshot }
}

export default createSearchGuard
